import { CalendarCheck, CalendarX } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { OUTBOUND_CALL_OUTCOME } from '@vagusx/shared';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

/**
 * Did this call actually book an appointment?
 *
 * The one commercially important fact about an outbound call, answerable at a
 * glance instead of by reading the headline sentence. Its own component for the
 * same reason as `CallStatusPill`: more than one surface will want it, and a
 * second hand-rolled version is exactly the divergence to avoid.
 *
 * ── Why booked is green and unbooked is NOT amber ────────────────────────
 * Plenty of calls legitimately book nothing — voicemail, a patient who
 * declined, a wrong number. Tinting all of those amber would put a warning
 * colour on calls where nothing went wrong, and a warning colour that fires on
 * the majority of rows stops being read at all. Severity is the STATUS pill's
 * job (green / amber / red). This pill answers a factual question, so the
 * crossed-out calendar carries the answer and the colour makes no claim.
 *
 * ── Source of truth ──────────────────────────────────────────────────────
 * The APPOINTMENT RECORD first, the disposition second — the same order, and
 * the same union, as `formatOutcome` on the API side: `booked = bookedOnThisCall
 * || outcomeCategory === SCHEDULED`.
 *
 * The disposition alone was wrong, and provably so. `deriveOutboundOutcome`
 * wrote `engaged_no_booking` for 6 of 6 production v2 calls that had committed
 * a real appointment (ADR-188), and inbound calls wrote no disposition at all
 * until inbound completions started recording one.
 * On such a call this pill read "No appointment booked" directly beside an
 * `outcomeLabel` that said "Appointment booked — …", because that label has
 * always consulted the record and this had no way to. `bookedOnThisCall` is
 * that record, derived per read from `scheduled_appointments.interaction_id`.
 *
 * The parameter is REQUIRED rather than defaulted: a caller that forgets it
 * should fail to compile, not silently get the disposition-only answer this
 * exists to replace.
 *
 * Never re-derive "was it booked" from the headline prose: the headline is
 * written by the evaluator, and this flag must not be able to disagree with the
 * appointment record.
 */

/** What the pill should say, or `null` when we cannot honestly say anything. */
export function bookingPillFor(
  outcomeCategory: string | null | undefined,
  bookedOnThisCall: boolean
): { booked: boolean; label: string } | null {
  // Proof outranks the disposition that failed to mention it.
  if (bookedOnThisCall) return { booked: true, label: 'Booked' };
  // No proof AND no disposition means we do not KNOW, and "No appointment
  // booked" is a claim. The record says no appointment was created on this
  // call, but the booking path is what stamps `interaction_id`, so an unstamped
  // row would read the same as a call that truly booked nothing. Same rule as
  // an unevaluated call rendering neutral rather than green: the surface never
  // asserts what it hasn't earned.
  if (!outcomeCategory) return null;
  return outcomeCategory === OUTBOUND_CALL_OUTCOME.SCHEDULED
    ? { booked: true, label: 'Booked' }
    : { booked: false, label: 'No appointment booked' };
}

interface BookingPillProps {
  outcomeCategory: string | null | undefined;
  /** From the evaluation response — the appointment record, not the disposition. */
  bookedOnThisCall: boolean;
  className?: string;
}

export function BookingPill({
  outcomeCategory,
  bookedOnThisCall,
  className,
}: BookingPillProps): React.ReactElement | null {
  const pill = bookingPillFor(outcomeCategory, bookedOnThisCall);
  if (!pill) return null;

  const Icon: LucideIcon = pill.booked ? CalendarCheck : CalendarX;
  return (
    <Badge
      variant={pill.booked ? 'outline' : 'secondary'}
      className={cn(
        'gap-1 shrink-0',
        // Same success token as the `all_clear` status pill (`call-status-tone.ts`)
        // so green means one thing across every pill on the page.
        pill.booked ? 'bg-success/15 text-success border-success/30' : 'text-muted-foreground',
        className
      )}
    >
      <Icon className="h-3 w-3" />
      {pill.label}
    </Badge>
  );
}
